import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Report from "@/services/analytics/report";

export interface CallConnectRateParams {
  organizationId: string;
  bucketOptionId?: string;
  period?: string | null;
  startDate?: string;
  endDate?: string;
  callDispositionId?: string;
  createdById?: string;
}

export function useCallConnectRate(
  params: Ref<CallConnectRateParams | null> | CallConnectRateParams | null
) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["callConnectRate", "disabled"];

    return [
      "callConnectRate",
      p.organizationId,
      p.bucketOptionId || "all",
      p.period || "",
      p.startDate || "",
      p.endDate || "",
      p.callDispositionId || "",
      p.createdById || "",
    ];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = {
        organizationId: p.organizationId,
      };

      if (p.bucketOptionId) {
        queryParams.bucketOptionId = p.bucketOptionId;
      }
      if (p.period) {
        queryParams.period = p.period;
      } else if (p.startDate && p.endDate) {
        queryParams.startDate = p.startDate;
        queryParams.endDate = p.endDate;
      }
      if (p.callDispositionId) {
        queryParams.callDispositionId = p.callDispositionId;
      }
      if (p.createdById) {
        queryParams.createdById = p.createdById;
      }

      const result = await Report.getCallConnectRate(queryParams);

      if (result.data?.status) {
        return result.data.data;
      }

      throw new Error(result.data?.msg || "Failed to load call connect rate metrics");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
